import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "../components/layout/AppLayout";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { ScoreRing } from "../components/ui/ScoreRing";
import { api, ApiError } from "../lib/api";
import type { Candidate, CompanyProfile, TeamFitTraits } from "../types";

type JobItem = { id: string; title: string; status: string; created_at: string };
type Tab = "overview" | "persona" | "analysis" | "resume" | "report";

const TABS: { id: Tab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "persona", label: "Persona Card" },
  { id: "analysis", label: "Team-fit Analysis" },
  { id: "resume", label: "Resume" },
  { id: "report", label: "Interview Report" },
];

const TRAIT_LABEL: Record<keyof TeamFitTraits, string> = {
  communication: "Communication",
  collaboration: "Collaboration",
  leadership: "Leadership",
  problemSolving: "Problem Solving",
  adaptability: "Adaptability",
  integrity: "Integrity",
};

function matchTier(score: number) {
  if (score >= 95) return { label: "Best Match", color: "bg-emerald-50 text-emerald-600" };
  if (score >= 85) return { label: "Great Match", color: "bg-teal/10 text-teal-dark" };
  return { label: "Good Match", color: "bg-slate-100 text-slate-500" };
}

export default function TeamMatch() {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Set when the backend blocks the action for a plan/quota reason (trial expired,
  // monthly search/invite cap hit, invite requires Team Fit+) so we can show an
  // upgrade CTA instead of a plain error string.
  const [upgradeReason, setUpgradeReason] = useState<string | null>(null);
  const [invitingId, setInvitingId] = useState<string | null>(null);
  const [invitedIds, setInvitedIds] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Candidate | null>(null);
  const [tab, setTab] = useState<Tab>("overview");
  const [company, setCompany] = useState<CompanyProfile | null>(null);
  const [jobs, setJobs] = useState<JobItem[]>([]);

  async function load(query?: string) {
    setLoading(true);
    setError(null);
    setUpgradeReason(null);
    try {
      const res = await api.get<{ items: Candidate[]; total: number }>(
        `/api/teamfit/candidates${query ? `?q=${encodeURIComponent(query)}` : ""}`
      );
      setCandidates(res.items);
      setSelected((prev) => prev ?? res.items[0] ?? null);
    } catch (err) {
      if (err instanceof ApiError && (err.code === "TRIAL_EXPIRED" || err.code === "SEARCH_QUOTA_EXCEEDED")) {
        setUpgradeReason(err.message);
      } else {
        setError(err instanceof Error ? err.message : "Failed to load candidates.");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    api.get<{ company: CompanyProfile | null }>("/api/company/me").then((res) => setCompany(res.company)).catch(() => undefined);
    api.get<{ items: JobItem[] }>("/api/company/jobs").then((res) => setJobs(res.items)).catch(() => undefined);
  }, []);

  async function invite(c: Candidate) {
    setInvitingId(c.userId);
    setError(null);
    setUpgradeReason(null);
    try {
      await api.post("/api/teamfit/invite", {
        userId: c.userId,
        careerFitScore: c.careerFitScore,
        teamFitScore: c.teamFitScore,
        reasons: c.reasons,
      });
      setInvitedIds((prev) => new Set(prev).add(c.userId));
    } catch (err) {
      if (err instanceof ApiError && (err.code === "UPGRADE_REQUIRED" || err.code === "INVITE_QUOTA_EXCEEDED")) {
        setUpgradeReason(err.message);
      } else {
        setError(err instanceof Error ? err.message : "Failed to send invitation.");
      }
    } finally {
      setInvitingId(null);
    }
  }

  const teamAvg = company ? Math.round(Object.values(company.cultureTraits).reduce((a, b) => a + b, 0) / 6) : 0;

  return (
    <AppLayout>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-navy sm:text-2xl">Team-fit Matchmaker</h1>
          <p className="mt-1 max-w-md text-xs text-slate-500">
            Find the right people who fit your team culture, not just their skills.
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {["Culture Fit", "Communication", "Leadership", "Creativity", "Problem Solving"].map((t) => (
              <span key={t} className="rounded-full border border-slate-200 px-2.5 py-1 text-[10px] font-medium text-slate-500">
                {t}
              </span>
            ))}
          </div>
        </div>
        <p className="max-w-xs text-right text-xs font-semibold leading-relaxed text-navy">
          Better Team Fit.<br />Greater Success.
        </p>
      </div>

      {/* Search bar */}
      <div className="flex gap-2">
        <input
          className="flex-1 rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-teal focus:outline-none"
          placeholder="Search by name, skill, or role"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(q)}
        />
        <Button onClick={() => load(q)}>Find Matching Candidates</Button>
      </div>

      {error && <Card className="mt-3 border border-coral/30 bg-coral/5 text-xs text-coral">{error}</Card>}
      {upgradeReason && (
        <Card className="mt-3 border border-rose-200 bg-rose-50 !p-3.5 text-rose-900">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs font-medium leading-relaxed">{upgradeReason}</p>
            <Link
              to="/pricing"
              className="flex-shrink-0 rounded-lg bg-navy px-3.5 py-2 text-xs font-bold text-white transition hover:bg-navy-light"
            >
              ⚡ 업그레이드 →
            </Link>
          </div>
        </Card>
      )}

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[300px_1fr_280px]">
        {/* Left: candidate list */}
        <Card className="max-h-[720px] overflow-y-auto border border-slate-100 !p-3">
          <p className="px-1 pb-2 text-xs font-semibold text-navy">Matching Candidates ({candidates.length})</p>
          {loading && <p className="px-1 text-xs text-slate-400">Loading candidates...</p>}
          {!loading && candidates.length === 0 && !error && (
            <p className="px-1 text-xs text-slate-400">
              No candidate matches found yet. Candidates will appear here once they create their Persona.
            </p>
          )}
          <div className="space-y-1.5">
            {candidates.map((c) => {
              const tier = matchTier(c.teamFitScore);
              const active = selected?.userId === c.userId;
              return (
                <button
                  key={c.userId}
                  onClick={() => {
                    setSelected(c);
                    setTab("overview");
                  }}
                  className={`w-full rounded-xl p-2.5 text-left transition ${active ? "bg-teal/10 ring-1 ring-teal/30" : "hover:bg-slate-50"}`}
                >
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-teal to-coral text-xs font-bold text-white">
                      {c.avatarUrl ? <img src={c.avatarUrl} alt={c.name} className="h-full w-full object-cover" /> : c.name[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-semibold text-navy">{c.name}</p>
                      <p className="truncate text-[10px] text-slate-400">{c.headline || "—"}</p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-xs font-bold text-teal-dark">{c.teamFitScore}%</p>
                    </div>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${tier.color}`}>{tier.label}</span>
                    {c.skills.slice(0, 2).map((s) => (
                      <span key={s} className="rounded-full bg-slate-50 px-1.5 py-0.5 text-[9px] text-slate-500">{s}</span>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Center: candidate detail */}
        <div className="min-w-0">
          {!selected ? (
            <Card className="flex h-full min-h-[400px] items-center justify-center border border-slate-100 text-center text-xs text-slate-400">
              Please select a candidate from the left list.
            </Card>
          ) : (
            <Card className="border border-slate-100">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-teal to-coral text-lg font-bold text-white">
                    {selected.avatarUrl ? (
                      <img src={selected.avatarUrl} alt={selected.name} className="h-full w-full object-cover" />
                    ) : (
                      selected.name[0]
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-navy">{selected.name}</p>
                    <p className="text-xs text-slate-500">{selected.headline || "No headline"}</p>
                    <p className="text-[11px] text-slate-400">{selected.location || "No location"}</p>
                  </div>
                </div>
                <div className="text-center">
                  <ScoreRing value={selected.teamFitScore} size={64} strokeWidth={6} />
                  <p className="mt-1 text-[10px] text-slate-400">Team-fit Score</p>
                </div>
              </div>

              <div className="mt-4 flex gap-1.5 overflow-x-auto border-b border-slate-100 pb-px">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`flex-shrink-0 whitespace-nowrap border-b-2 px-2 pb-2 text-xs font-semibold transition ${
                      tab === t.id ? "border-teal text-teal-dark" : "border-transparent text-slate-400 hover:text-navy"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="mt-4">
                {tab === "overview" && (
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs font-semibold text-navy">Skills</p>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {selected.skills.map((s) => (
                          <span key={s} className="rounded-full bg-slate-50 px-2 py-1 text-[11px] text-slate-500">{s}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-navy">Why {selected.name.split(" ")[0]} Fits Your Team</p>
                      <ul className="mt-1.5 space-y-1.5">
                        {selected.reasons.length === 0 && <li className="text-[11px] text-slate-400">No match rationale analyzed yet.</li>}
                        {selected.reasons.map((r, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-500">
                            <span className="mt-0.5 text-teal">✓</span>{r}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-xl bg-slate-50 p-3 text-center">
                        <p className="text-lg font-bold text-navy">{selected.teamFitScore}%</p>
                        <p className="text-[10px] text-slate-400">Team-fit Score</p>
                      </div>
                      <div className="rounded-xl bg-slate-50 p-3 text-center">
                        <p className="text-lg font-bold text-navy">{selected.careerFitScore}%</p>
                        <p className="text-[10px] text-slate-400">Career Fit Score</p>
                      </div>
                    </div>
                  </div>
                )}

                {tab === "persona" && (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-500">
                      Public Persona Card summary for {selected.name}. The full card will be shared once team match request is accepted.
                    </p>
                    <div className="rounded-xl bg-slate-50 p-3">
                      <p className="text-xs font-semibold text-navy">{selected.headline || "No headline"}</p>
                      <p className="mt-1 text-[11px] text-slate-400">{selected.location || "No location"}</p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {selected.skills.map((s) => (
                          <span key={s} className="rounded-full bg-white px-2 py-1 text-[10px] text-slate-500">{s}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {tab === "analysis" && (
                  <div className="space-y-4">
                    <p className="text-xs text-slate-500">
                      Analyzed alignment with your team culture profile divided into Career Fit and Team-fit.
                    </p>
                    <ScoreBar label="Team-fit Score" value={selected.teamFitScore} />
                    <ScoreBar label="Career Fit Score" value={selected.careerFitScore} />
                    {selected.reasons.length > 0 && (
                      <div>
                        <p className="mb-1.5 text-xs font-semibold text-navy">Key Findings</p>
                        <ul className="space-y-1">
                          {selected.reasons.map((r, i) => (
                            <li key={i} className="text-[11px] leading-relaxed text-slate-500">• {r}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {tab === "resume" && (
                  <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center">
                    <p className="text-xs text-slate-400">
                      Full resume will be shared when candidate accepts interview invitation.<br />
                      Currently showing skills and profile summary.
                    </p>
                  </div>
                )}

                {tab === "report" && (
                  <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center">
                    <p className="text-xs text-slate-400">
                      No AI interview report generated yet.<br />
                      Invite this candidate to an AI interview to generate a report.
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-5 flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
                <Button
                  variant="secondary"
                  loading={invitingId === selected.userId}
                  disabled={invitedIds.has(selected.userId)}
                  onClick={() => invite(selected)}
                >
                  {invitedIds.has(selected.userId) ? "✓ Invited" : "Invite to Interview →"}
                </Button>
              </div>
            </Card>
          )}
        </div>

        {/* Right: team culture + jobs + quick actions */}
        <div className="space-y-4">
          <Card className="border border-slate-100">
            <p className="text-xs font-semibold text-navy">Your Team Culture Profile</p>
            {company ? (
              <>
                <div className="mt-3 flex justify-center">
                  <ScoreRing value={teamAvg} size={80} strokeWidth={7} label="Team-fit Score" />
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {company.cultureValues.map((v) => (
                    <span key={v} className="rounded-full bg-teal/10 px-2 py-1 text-[10px] font-medium text-teal-dark">{v}</span>
                  ))}
                </div>
                <div className="mt-3 space-y-2">
                  {(Object.entries(company.cultureTraits) as [keyof TeamFitTraits, number][]).map(([k, v]) => (
                    <div key={k}>
                      <div className="flex justify-between text-[10px] text-slate-500">
                        <span>{TRAIT_LABEL[k]}</span><span>{v}%</span>
                      </div>
                      <div className="mt-0.5 h-1.5 rounded-full bg-slate-100">
                        <div className="h-1.5 rounded-full bg-teal" style={{ width: `${v}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
                {(company.communicationStyle || company.leadershipStyle) && (
                  <div className="mt-3 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3 text-[10px]">
                    <div>
                      <p className="text-slate-400">Communication</p>
                      <p className="font-medium text-navy">{company.communicationStyle || "—"}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Leadership</p>
                      <p className="font-medium text-navy">{company.leadershipStyle || "—"}</p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="mt-3 text-center">
                <p className="text-[11px] text-slate-400">No Team Culture Profile set yet.</p>
                <Link to="/company/culture" className="mt-2 inline-block text-[11px] font-semibold text-teal-dark">
                  Set Team Culture →
                </Link>
              </div>
            )}
          </Card>

          <Card className="border border-slate-100">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-navy">Active Job Posts</p>
              <Link to="/company" className="text-[10px] font-medium text-teal-dark">View All →</Link>
            </div>
            <div className="mt-3 space-y-2">
              {jobs.length === 0 && <p className="text-[11px] text-slate-400">No active job postings.</p>}
              {jobs.slice(0, 3).map((j) => (
                <div key={j.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-2.5 py-2">
                  <p className="truncate text-[11px] font-medium text-navy">{j.title}</p>
                  <span className="flex-shrink-0 rounded-full bg-white px-1.5 py-0.5 text-[9px] font-semibold text-slate-500">{j.status}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="border border-slate-100">
            <p className="text-xs font-semibold text-navy">Quick Actions</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Link to="/company/culture" className="rounded-xl bg-slate-50 px-3 py-3 text-center text-[11px] font-medium text-navy transition hover:bg-slate-100">
                🎯<br />Team Culture
              </Link>
              <Link to="/company/interview" className="rounded-xl bg-slate-50 px-3 py-3 text-center text-[11px] font-medium text-navy transition hover:bg-slate-100">
                💬<br />AI Interview
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}

function ScoreBar({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="flex justify-between text-xs">
        <span className="font-medium text-navy">{label}</span>
        <span className="font-semibold text-teal-dark">{value}%</span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-slate-100">
        <div className="h-2 rounded-full bg-teal" style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}
